<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Groups"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :searchValue="search"
      @update:searchValue="search = $event"
      @search="applyFilters"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div class="kpi-grid">
        <MetricCard label="Total Groups" :value="summary.totalGroups" :state="summaryState" />
        <MetricCard label="Security Groups" :value="summary.securityGroups" :state="summaryState" />
        <MetricCard label="M365 Groups" :value="summary.m365Groups" :state="summaryState" />
        <MetricCard label="Dynamic Groups" :value="summary.dynamicGroups" :state="summaryState" />
        <MetricCard label="Assigned Groups" :value="summary.assignedGroups" :state="summaryState" />
        <MetricCard label="Licensed Groups" :value="summary.licensedGroups" :state="summaryState" />
        <MetricCard label="Ownerless Groups" :value="summary.ownerlessGroups" :state="summaryState" />
        <MetricCard label="Empty Groups" :value="summary.emptyGroups" :state="summaryState" />
        <MetricCard label="Groups with Guests" :value="summary.groupsWithGuests" :state="summaryState" />
        <MetricCard label="Role-Assignable" :value="summary.roleAssignableGroups" :state="summaryState" />
        <MetricCard label="Groups with Findings" :value="summary.groupsWithFindings" :state="summaryState" />
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-if="tableState === 'loading'" class="loading-state">Loading groups...</div>
      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No groups found" description="No groups match the current filters." />
      </div>
      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Group</th>
              <th>Type</th>
              <th>Membership</th>
              <th>Members</th>
              <th>Guests</th>
              <th>Owners</th>
              <th>Licensed</th>
              <th>Role Assignable</th>
              <th>Visibility</th>
              <th>Findings</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in groups" :key="group.groupId">
              <td>
                <div class="group-name">{{ group.displayName }}</div>
                <div class="group-mail">{{ group.mail || '--' }}</div>
              </td>
              <td>{{ group.type }}</td>
              <td>{{ group.membershipType }}</td>
              <td>{{ formatCount(group.directMemberCount) }}</td>
              <td>{{ formatCount(group.guestMemberCount) }}</td>
              <td>{{ formatCount(group.ownerCount) }}</td>
              <td>{{ formatCount(group.assignedLicenceCount) }}</td>
              <td>{{ group.isAssignableToRole ? 'Yes' : 'No' }}</td>
              <td>{{ groupVisibility(group) }}</td>
              <td>
                <FindingSeverityChip v-if="group.findingCount > 0" :severity="group.worstSeverity" />
                <span v-else>--</span>
              </td>
              <td>
                <button class="detail-btn" @click="openGroup(group.groupId)">Details</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>
    </div>

    <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <h2>{{ drawerGroup.displayName || 'Group Details' }}</h2>
          <button class="drawer-close" @click="closeDrawer">&times;</button>
        </div>

        <div v-if="drawerState === 'loading'" class="drawer-loading">Loading...</div>
        <div v-else-if="drawerState === 'error'" class="drawer-error">{{ drawerError }}</div>
        <div v-else class="drawer-body">
          <div class="drawer-details">
            <div class="detail-row"><span class="detail-label">Type</span><span class="detail-value">{{ drawerGroup.type }}</span></div>
            <div class="detail-row"><span class="detail-label">Membership</span><span class="detail-value">{{ drawerGroup.membershipType }}</span></div>
            <div class="detail-row"><span class="detail-label">Visibility</span><span class="detail-value">{{ drawerGroup.visibility || '--' }}</span></div>
            <div class="detail-row"><span class="detail-label">Role Assignable</span><span class="detail-value">{{ drawerGroup.isAssignableToRole ? 'Yes' : 'No' }}</span></div>
            <div class="detail-row"><span class="detail-label">Mail</span><span class="detail-value">{{ drawerGroup.mail || '--' }}</span></div>
            <div class="detail-row"><span class="detail-label">Created</span><span class="detail-value">{{ drawerGroup.createdDateTime || '--' }}</span></div>
            <div class="detail-row"><span class="detail-label">Description</span><span class="detail-value">{{ drawerGroup.description || '--' }}</span></div>
            <div class="detail-row"><span class="detail-label">Direct Members</span><span class="detail-value">{{ drawerGroup.directMemberCount }}</span></div>
            <div class="detail-row"><span class="detail-label">Guest Members</span><span class="detail-value">{{ drawerGroup.guestMemberCount }}</span></div>
            <div class="detail-row"><span class="detail-label">Owners</span><span class="detail-value">{{ drawerGroup.ownerCount }}</span></div>
          </div>

          <div class="drawer-section" v-if="drawerOwners.length > 0">
            <h3>Owners</h3>
            <div class="member-list">
              <div v-for="owner in drawerOwners" :key="owner.memberId" class="member-item">
                <span class="member-name">{{ owner.displayName }}</span>
                <span class="member-upn">{{ owner.userPrincipalName }}</span>
              </div>
            </div>
          </div>

          <div class="drawer-section" v-if="drawerMembers.length > 0">
            <h3>Members</h3>
            <div class="member-list">
              <div v-for="member in drawerMembers" :key="member.memberId" class="member-item">
                <span class="member-name">{{ member.displayName }}</span>
                <span class="member-upn">{{ member.userPrincipalName }}</span>
                <span class="member-type-badge">{{ member.memberType }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import FindingSeverityChip from '@/components/entra/common/FindingSeverityChip.vue';
import { getEntraGroups, getEntraGroupDetails, getEntraGroupMembers, getEntraGroupOwners } from '@/services/entra-api.js';
import { formatCount } from '@/services/entra-utils.js';

export default {
  name: 'EntraGroups',
  components: { EntraPageHeader, MetricCard, EmptyState, FindingSeverityChip },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      search: '',
      summary: { totalGroups: 0, securityGroups: 0, m365Groups: 0, dynamicGroups: 0, assignedGroups: 0, licensedGroups: 0, ownerlessGroups: 0, emptyGroups: 0, groupsWithGuests: 0, roleAssignableGroups: 0, groupsWithFindings: 0 },
      summaryState: 'loading',
      groups: [], tableState: 'loading',
      page: 1, totalPages: 1,
      error: '',
      showDrawer: false,
      drawerState: 'loading',
      drawerError: '',
      drawerGroup: {},
      drawerMembers: [],
      drawerOwners: []
    };
  },
  methods: {
    formatCount: formatCount,
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.summaryState = 'empty';
        this.tableState = 'empty';
        return;
      }

      getEntraGroups(this.tenantId, { page: this.page, pageSize: 50, search: this.search }).then(function(data) {
        var groups = data.items || [];
        self.groups = groups;
        self.totalPages = Math.ceil((data.total || 0) / 50);
        self.tableState = groups.length === 0 ? 'empty' : 'loaded';
        self.summary = {
          totalGroups: data.total || 0,
          securityGroups: groups.filter(function(g) { return g.type === 'Security'; }).length,
          m365Groups: groups.filter(function(g) { return g.type === 'Microsoft 365'; }).length,
          dynamicGroups: groups.filter(function(g) { return g.membershipType === 'Dynamic'; }).length,
          assignedGroups: groups.filter(function(g) { return g.membershipType === 'Assigned'; }).length,
          licensedGroups: groups.filter(function(g) { return g.assignedLicenceCount > 0; }).length,
          ownerlessGroups: groups.filter(function(g) { return g.ownerCount === 0; }).length,
          emptyGroups: groups.filter(function(g) { return g.directMemberCount === 0 && g.guestMemberCount === 0; }).length,
          groupsWithGuests: groups.filter(function(g) { return g.guestMemberCount > 0; }).length,
          roleAssignableGroups: groups.filter(function(g) { return g.isAssignableToRole; }).length,
          groupsWithFindings: groups.filter(function(g) { return g.findingCount > 0; }).length
        };
        self.summaryState = 'loaded';
      }).catch(function(err) { self.error = err.message; self.tableState = 'error'; });
    },
    applyFilters: function() { this.page = 1; this.loadData(); },
    goToPage: function(p) { this.page = p; this.loadData(); },
    handleExport: function() {},
    openGroup: function(groupId) {
      var self = this;
      this.showDrawer = true;
      this.drawerState = 'loading';
      this.drawerError = '';
      this.drawerGroup = {};
      this.drawerMembers = [];
      this.drawerOwners = [];

      getEntraGroupDetails(this.tenantId, groupId).then(function(data) {
        if (data.error) {
          self.drawerError = data.error.message || 'Group not found.';
          self.drawerState = 'error';
          return;
        }
        self.drawerGroup = data;
        self.drawerState = 'loaded';

        getEntraGroupOwners(self.tenantId, groupId).then(function(ownerData) {
          self.drawerOwners = ownerData.items || [];
        }).catch(function() {});

        getEntraGroupMembers(self.tenantId, groupId, { pageSize: 200 }).then(function(memberData) {
          self.drawerMembers = memberData.items || [];
        }).catch(function() {});
      }).catch(function(err) {
        self.drawerError = err.message || 'Failed to load group details.';
        self.drawerState = 'error';
      });
    },
    closeDrawer: function() {
      this.showDrawer = false;
    },
    groupVisibility: function(group) {
      if (group.visibility) return group.visibility;
      if (group.type === 'Security') return 'N/A';
      if (group.type === 'DynamicMembership') return 'N/A';
      return '--';
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }
.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 12px; margin-bottom: 24px; }
.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.875rem; }
.loading-state { padding: 48px; text-align: center; color: #94a3b8; }
.table-wrapper { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 12px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.group-name { font-weight: 600; color: #1e293b; }
.group-mail { font-size: 0.6875rem; color: #94a3b8; }
.detail-btn { background: none; border: 1px solid #cbd5e1; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.detail-btn:hover { background: #f1f5f9; }
.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px; border-top: 1px solid #e2e8f0; }
.page-btn { background: transparent; border: 1px solid #cbd5e1; padding: 6px 14px; border-radius: 6px; font-size: 0.8125rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.8125rem; color: #64748b; }

.drawer-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(0,0,0,0.3); z-index: 100; display: flex; justify-content: flex-end; }
.drawer { width: 520px; max-width: 90vw; height: 100%; background: #ffffff; overflow-y: auto; box-shadow: -4px 0 24px rgba(0,0,0,0.12); animation: drawer-slide 0.2s ease-out; }
@keyframes drawer-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
.drawer-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: #ffffff; z-index: 1; }
.drawer-header h2 { margin: 0; font-size: 1.125rem; font-weight: 600; color: #1e293b; }
.drawer-close { background: none; border: none; font-size: 1.5rem; color: #94a3b8; cursor: pointer; padding: 0 4px; line-height: 1; }
.drawer-close:hover { color: #475569; }
.drawer-loading { padding: 24px; text-align: center; color: #94a3b8; }
.drawer-error { padding: 24px; color: #991b1b; }
.drawer-body { padding: 24px; }
.drawer-details { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
.detail-row { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 8px; border-bottom: 1px solid #f1f5f9; }
.detail-label { font-size: 0.75rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; }
.detail-value { font-size: 0.8125rem; color: #334155; text-align: right; max-width: 60%; }
.drawer-section { margin-top: 20px; }
.drawer-section h3 { font-size: 0.875rem; font-weight: 600; color: #475569; margin: 0 0 10px 0; }
.member-list { display: flex; flex-direction: column; gap: 6px; }
.member-item { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px solid #f8fafc; }
.member-name { font-size: 0.8125rem; color: #334155; font-weight: 500; }
.member-upn { font-size: 0.6875rem; color: #94a3b8; }
.member-type-badge { margin-left: auto; font-size: 0.625rem; padding: 1px 6px; border-radius: 3px; background: #f1f5f9; color: #64748b; text-transform: uppercase; font-weight: 600; }
</style>
